/* eslint-disable react/prop-types */
import React from "react";
import { useTranslation } from "react-i18next";
import Drawer from "@material-ui/core/Drawer";
import Tooltip from "@material-ui/core/Tooltip";
import TableContainer from "@material-ui/core/TableContainer";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import PlayArrow from "@material-ui/icons/PlayArrow";
import Pause from "@material-ui/icons/Pause";
import FolderOpen from "@material-ui/icons/FolderOpen";
import DeleteSweep from "@material-ui/icons/DeleteSweep";
import ArrowUpward from "@material-ui/icons/ArrowUpward";
import ArrowDownward from "@material-ui/icons/ArrowDownward";
import HighlightOff from "@material-ui/icons/HighlightOff";
import CheckBox from "@material-ui/icons/CheckBox";
import CheckBoxOutlineBlank from "@material-ui/icons/CheckBoxOutlineBlank";
import ReactSelect from "react-select";
import Button from "../../../components/CustomButtons/Button";
import ModalHeader from "../../../components/ModalHeader";
import CustomLinearProgress from "../../../components/CustomLinearProgress/CustomLinearProgress";
import { getAcceptExtensions, formatFileTypeLabel } from "./utils/fileTypes";

export default function UploadQueueDrawer({
  classes,
  selectStyles,
  getDate,
  selectedMonthData,
  inputsStatus,
  queueDrawerOpen,
  setQueueDrawerOpen,
  isQueueActive,
  setQueueRunning,
  canRunQueue,
  uploadQueueItems,
  setQueuePauseAfterCurrent,
  queueIgnoreUploaded,
  setQueueIgnoreUploaded,
  clearQueue,
  queueFolderInputRef,
  handleQueueFolderSelected,
  queueSelectResetKey,
  queueNewSelectedInputId,
  setQueueNewSelectedInputId,
  handleQueuePickFile,
  queueFileInputRef,
  queuePendingInputIdRef,
  addItemToQueue,
  setQueueSelectResetKey,
  moveItemUp,
  moveItemDown,
  removeItemFromQueue,
}) {
  const { t, i18n } = useTranslation();
  const periodLabel = selectedMonthData
    ? getDate(
        (selectedMonthData.year * 100 + selectedMonthData.month).toString(),
        "YYYYMM",
        "MMM-YY",
      )
    : "";
  const handleClose = () => {
    if (isQueueActive) return;
    setQueueDrawerOpen(false);
  };

  return (
    <Drawer
      anchor={"right"}
      open={queueDrawerOpen}
      onClose={handleClose}
      disableEscapeKeyDown={isQueueActive}
      BackdropProps={{ style: { backgroundColor: "#000", opacity: "0.1" } }}
      PaperProps={{
        style: {
          borderRadius: "10px",
          margin: "1vh 1vh 1vh 0",
          height: "98vh",
          width: "720px",
          overflowX: "hidden",
          paddingBottom: "8px",
        },
      }}
    >
      <ModalHeader
        title={t("modeling_inputs_v2.upload_queue")}
        subtitleParts={[
          t("modeling_inputs_v2.upload_queue_start"),
          periodLabel ? <strong>{periodLabel}</strong> : null,
        ].filter(Boolean)}
        pills={[
          {
            key: "items",
            label: `${uploadQueueItems.length} ${t("common.items")}`,
          },
          isQueueActive
            ? {
                key: "active",
                label: t("modeling_inputs_v2.processing"),
                tone: "warning",
                dot: true,
              }
            : null,
        ].filter(Boolean)}
        onClose={handleClose}
        closeDisabled={isQueueActive}
        closeLabel={t("common.close")}
        variant="drawer"
      />
      <div className={classes.queueDrawerContent}>
        <div className={classes.queueButtonsRow}>
          <Tooltip title={t("modeling_inputs_v2.upload_queue_start")}>
            <span>
              <Button
                color={"client"}
                size={"sm"}
                justIcon
                disabled={
                  isQueueActive ||
                  !canRunQueue() ||
                  uploadQueueItems.length === 0
                }
                onClick={() => setQueueRunning(true)}
              >
                <PlayArrow />
              </Button>
            </span>
          </Tooltip>
          <Tooltip
            title={t("modeling_inputs_v2.upload_queue_pause_after_current")}
          >
            <span>
              <Button
                color={"system_gray"}
                size={"sm"}
                justIcon
                onClick={() => setQueuePauseAfterCurrent((v) => !v)}
                disabled={!isQueueActive}
              >
                <Pause />
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={t("modeling_inputs_v2.upload_queue_import_folder")}>
            <span>
              <Button
                color={"system_gray"}
                size={"sm"}
                justIcon
                disabled={isQueueActive}
                onClick={() =>
                  queueFolderInputRef.current &&
                  queueFolderInputRef.current.click()
                }
              >
                <FolderOpen />
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={t("modeling_inputs_v2.upload_queue_clear")}>
            <span>
              <Button
                color={"danger"}
                size={"sm"}
                justIcon
                onClick={clearQueue}
                disabled={isQueueActive}
              >
                <DeleteSweep />
              </Button>
            </span>
          </Tooltip>
          {/* directory + webkitdirectory: folder picker (non-standard DOM attrs) */}
          <input
            type="file"
            webkitdirectory=""
            // eslint-disable-next-line react/no-unknown-property -- directory needed with webkitdirectory for folder selection
            directory=""
            multiple
            style={{ display: "none" }}
            ref={(el) => (queueFolderInputRef.current = el)}
            onChange={(e) => handleQueueFolderSelected(e)}
          />
          <div
            className={classes.queueCheckboxWrapper}
            onClick={() => setQueueIgnoreUploaded(!queueIgnoreUploaded)}
          >
            {queueIgnoreUploaded ? (
              <CheckBox className={classes.queueCheckboxIcon} />
            ) : (
              <CheckBoxOutlineBlank className={classes.queueCheckboxIcon} />
            )}
            <span className={classes.queueCheckboxLabel}>
              {t("modeling_inputs_v2.upload_queue_ignore_uploaded")}
            </span>
          </div>
        </div>

        <div className={classes.queueManualAddRow}>
          <div className={classes.queueSelectWrapper}>
            <ReactSelect
              key={queueSelectResetKey}
              isDisabled={isQueueActive}
              placeholder={t("modeling_inputs_v2.select_input")}
              value={(() => {
                const inp = inputsStatus
                  .flatMap((g) => g.inputs || [])
                  .find((x) => x.input_id == queueNewSelectedInputId);
                return inp
                  ? {
                      value: inp.input_id,
                      label:
                        (inp.input_name?.[i18n.language] ||
                          inp.input_name?.en ||
                          inp.input_name) +
                        ` (${formatFileTypeLabel(inp.expected_file_type)})`,
                    }
                  : null;
              })()}
              onChange={(opt) => {
                const selectedId = opt ? opt.value : null;
                if (selectedId) {
                  handleQueuePickFile(selectedId);
                } else {
                  setQueueNewSelectedInputId(null);
                }
              }}
              options={(() => {
                const allInputs = inputsStatus
                  .flatMap((g) => g.inputs || [])
                  .filter((inp) => inp.source === "file_upload");
                const filtered = queueIgnoreUploaded
                  ? allInputs.filter(
                      (inp) => inp.stages?.data_source !== "success",
                    )
                  : allInputs;
                return filtered.map((inp) => ({
                  value: inp.input_id,
                  label:
                    (inp.input_name?.[i18n.language] ||
                      inp.input_name?.en ||
                      inp.input_name) +
                    ` (${formatFileTypeLabel(inp.expected_file_type)})`,
                }));
              })()}
              styles={{
                ...selectStyles,
                menu: (base) => ({ ...base, zIndex: 2000 }),
              }}
            />
          </div>
          <input
            type="file"
            ref={(el) => (queueFileInputRef.current = el)}
            style={{ display: "none" }}
            accept={(() => {
              const effectiveInputId =
                queuePendingInputIdRef.current ?? queueNewSelectedInputId;
              const inp = inputsStatus
                .flatMap((g) => g.inputs || [])
                .find((x) => x.input_id == effectiveInputId);
              if (!inp) return "";
              return getAcceptExtensions(inp.expected_file_type);
            })()}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) {
                setQueueNewSelectedInputId(null);
                setQueueSelectResetKey((k) => k + 1);
                queuePendingInputIdRef.current = null;
                e.target.value = "";
                return;
              }
              const effectiveInputId =
                queuePendingInputIdRef.current ?? queueNewSelectedInputId;
              const inp = inputsStatus
                .flatMap((g) => g.inputs || [])
                .find((x) => x.input_id == effectiveInputId);
              if (!inp) return;
              addItemToQueue({
                input_control_id: inp.input_control_id,
                input_id: inp.input_id,
                input_name: inp.input_name,
                expected_file_type: inp.expected_file_type,
                file,
              });
              setQueueNewSelectedInputId(null);
              setQueueSelectResetKey((k) => k + 1);
              queuePendingInputIdRef.current = null;
              e.target.value = "";
            }}
          />
        </div>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("modeling_inputs_v2.input")}</TableCell>
                <TableCell>{t("modeling_inputs_v2.file")}</TableCell>
                <TableCell align="center">
                  {t("modeling_inputs_v2.actions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {uploadQueueItems.map((it, idx) => (
                <TableRow key={idx}>
                  <TableCell className={classes.queueTableCell}>
                    {it.input_name?.[i18n.language] ||
                      it.input_name?.en ||
                      it.input_name}
                  </TableCell>
                  <TableCell className={classes.queueTableCell}>
                    {it.file?.name || "-"}
                  </TableCell>
                  <TableCell
                    align="center"
                    className={classes.queueTableCellActions}
                  >
                    <div className={classes.queueActionsContainer}>
                      <div className={classes.queueActionsButtons}>
                        <Button
                          justIcon
                          size={"sm"}
                          color="system_gray"
                          onClick={() => moveItemUp(idx)}
                          disabled={isQueueActive || idx === 0}
                        >
                          <ArrowUpward style={{ width: 16, height: 16 }} />
                        </Button>
                        <Button
                          justIcon
                          size={"sm"}
                          color="system_gray"
                          onClick={() => moveItemDown(idx)}
                          disabled={
                            isQueueActive || idx === uploadQueueItems.length - 1
                          }
                        >
                          <ArrowDownward style={{ width: 16, height: 16 }} />
                        </Button>
                        <Button
                          justIcon
                          size={"sm"}
                          color="danger"
                          onClick={() => removeItemFromQueue(idx)}
                          disabled={isQueueActive}
                        >
                          <HighlightOff style={{ width: 16, height: 16 }} />
                        </Button>
                      </div>
                      <div className={classes.queueProgressBarWrapper}>
                        <CustomLinearProgress
                          variant={"determinate"}
                          color="client"
                          value={it.progress || 0}
                          style={{ height: 4, marginBottom: 2 }}
                          rootClasses={classes.customProgressBarRoot}
                          barClasses={classes.customProgressBarBar}
                        />
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {uploadQueueItems.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={3}
                    align="center"
                    className={classes.queueEmptyMessage}
                  >
                    {t("modeling_inputs_v2.upload_queue_empty")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </Drawer>
  );
}
